import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useSiteConfig, useScreenSize } from '@/hooks';
import { storage, getUID, encode, decode, C } from '@/utils';
import socket from '@/utils/socketInstance';
import { fetchGisGames } from '@/services/api';
import HomeBannerStrip from '@/components/home/HomeBannerStrip/HomeBannerStrip';
import LatestWins from '@/components/home/LatestWins/LatestWins';
import HotGames from '@/components/home/HotGames/HotGames';
import CategoryStrip from '@/components/home/CategoryStrip/CategoryStrip';
import Sports from '@/components/home/Sports/Sports';
import GameCards from '@/components/home/GameCards/GameCards';
import DealerCategories from '@/components/home/DealerCategories/DealerCategories';
import WelcomeBanner from '@/components/home/WelcomeBanner/WelcomeBanner';
import WelcomeBannerRightPanel from '@/components/home/WelcomeBanner/WelcomeBannerRightPanel';
import StatsCard from '@/components/home/WelcomeBanner/StatsCard/StatsCard';
import CryptoStrip from '@/components/home/CryptoStrip/CryptoStrip';
import styles from './Home.module.css';



/**PW
 * Home Page – CRYPTMET Sitemap
 * Same sections visible for everyone (logged in or not).
 */
const Home = () => {
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const { isMobile, isTablet } = useScreenSize();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const socketListenerRef = useRef(null);
  const hasSetupSocketRef = useRef(false);

  const latestWinsProviders = ['Betsoft', 'Igrosoft', 'OneTouch', 'BFGames', 'BoomingGames'];
  const accumulatedGamesRef = useRef([]);
  const [latestWinsGames, setLatestWinsGames] = useState([]);

  const latestWinsQueries = useQueries({
    queries: latestWinsProviders.map((provider) => ({
      queryKey: ['latestWinsGames', provider, isMobile],
      queryFn: async () => {
        const result = await fetchGisGames(provider, 1, 20, '', isMobile);
        // Throw if empty so React Query retries and keeps previous cached data
        if (!result?.games?.length) {
          throw new Error(`No games for ${provider}`);
        }
        return result;
      },
      staleTime: 300000,
      refetchInterval: 300000,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
      placeholderData: (prev) => prev,
    })),
  });

  // Derive a stable dependency from query data timestamps
  const latestWinsDataUpdatedAt = latestWinsQueries.map(q => q.dataUpdatedAt).join(',');

  // Accumulate games into ref + state so it never vanishes
  useEffect(() => {
    const newGames = [];
    latestWinsQueries.forEach((query) => {
      if (query.data?.games && Array.isArray(query.data.games) && query.data.games.length > 0) {
        query.data.games.forEach((game) => {
          newGames.push({
            id: game.uuid,
            uuid: game.uuid,
            game_uid: game.uuid,
            name: game.name,
            game_name: game.name,
            game_icon: game.image,
            image: game.image,
            game_type: game.type,
            provider: game.provider || game.providerName,
            source: 'gis',
          });
        });
      }
    });

    if (newGames.length > 0) {
      const existingIds = new Set(accumulatedGamesRef.current.map((g) => g.uuid));
      const uniqueNew = newGames.filter((g) => !existingIds.has(g.uuid));
      if (uniqueNew.length > 0) {
        accumulatedGamesRef.current = [...accumulatedGamesRef.current, ...uniqueNew];
        setLatestWinsGames([...accumulatedGamesRef.current]);
      } else if (latestWinsGames.length === 0 && accumulatedGamesRef.current.length > 0) {
        setLatestWinsGames([...accumulatedGamesRef.current]);
      }
    }
  }, [latestWinsDataUpdatedAt]);

  // Keep loading true until we actually have accumulated data
  const isLoadingLatestWins = accumulatedGamesRef.current.length === 0 && latestWinsQueries.some((query) => query.isLoading || query.isFetching);

  useEffect(() => {
    const logged = storage.getKey('logged') === true || localStorage.getItem('logged') === 'true';
    setIsLoggedIn(logged);
  }, []);

  useEffect(() => {
    if (hasSetupSocketRef.current) return;
    let isMounted = true;
    hasSetupSocketRef.current = true;
    const uid = getUID();
    if (!uid) return;

    const handleUserInfo = (data) => {
      if (!isMounted) return;
      try {
        const decodedData = decode(data);
        if (decodedData && decodedData.status) setUserData(decodedData);
      } catch (error) {
        console.error('Error decoding user info:', error);
      }
    };

    socket.on(C.USER_INFO, handleUserInfo);
    socketListenerRef.current = handleUserInfo;
    socket.emit(C.USER_INFO, encode({
      id: uid,
      coin: storage.getKey('coin') || '',
      rate: null,
      game: 'all',
      first: true,
    }));

    return () => {
      isMounted = false;
      hasSetupSocketRef.current = false;
      if (socket && socketListenerRef.current) {
        try {
          socket.off(C.USER_INFO, socketListenerRef.current);
          socketListenerRef.current = null;
        } catch (error) {
          console.error('Error cleaning up socket listener:', error);
        }
      }
    };
  }, []);

  return (
    <div className={styles.home}>
      {/* Hero Banner */}
      <WelcomeBanner />

      {/* Live Activity Ticker */}
      <section className={styles.section}>
        <LatestWins
          all={latestWinsGames}
          slots={latestWinsGames}
          liveCasino={latestWinsGames}
          providers={latestWinsProviders}
          isLoading={isLoadingLatestWins}
        />
      </section>

      {/* Stats Card (mobile only — after Latest Wins) */}
      {(isMobile || isTablet) && (
        <section className={styles.section}>
          <StatsCard />
        </section>
      )}

      {/* Vault Pro + P2P Trade (desktop only — positioned here) */}
      {!isMobile && !isTablet && (
        <section className={styles.section}>
          <HomeBannerStrip />
        </section>
      )}

      {/* Sports / E-sport / Casino strip (Swiper) – middle of home */}
      <section className={styles.section}>
        <CategoryStrip />
      </section>
      {/* Dealer Categories Strip */}
      <section className={styles.section}>
        <DealerCategories />
      </section>

      {/* Trending Games */}
      <section className={styles.section}>
        <HotGames />
      </section>
   
      {/* Vault Pro + P2P Trade (mobile position — after HotGames) */}
      {(isMobile || isTablet) && (
        <section className={styles.section}>
          <HomeBannerStrip />
        </section>
      )}

      {/* Live Casino */}
      <section className={styles.section}>
        <GameCards
          title={t('casino.liveCasino')}
          gameTypes={['live dealer', 'blackjack', 'teen patti']}
        />
      </section>


      {/* Sports */}
      <section className={styles.section}>
        <Sports />
      </section>

      {/* Popular Slots */}
      <section className={styles.section}>
        <GameCards
          title={t('casino.slots')}
          gameTypes={['slots']}
        />
      </section>
         {/* P2P Crypto Trading Strip */}
         <section className={styles.section}>
        <CryptoStrip />
      </section>



      {/* Crash Games */}
      <section className={styles.section}>
        <GameCards
          title={t('casino.crashGames')}
          gameTypes={['crash']}
        />
      </section>


      {/* Live Activity / P2P strip – mid-page on mobile only */}
      {(isMobile || isTablet) && (
        <section className={styles.section}>
          <WelcomeBannerRightPanel />
        </section>
      )}



    </div>
  );
};

export default Home;
